/**
 * Predefined Filters Frontend JavaScript
 *
 * Handles coordination of multiple filters in a predefined filter group.
 * Ensures filters work together and trigger product grid updates.
 *
 * @package Storedash
 * @since 3.4.0
 */

(function() {
    'use strict';

    /**
     * Predefined Filters Handler
     * 
     * Note: Individual filters are initialized by filters.js automatically
     * This class just handles group-level coordination and layout
     */
    class PredefinedFilters {
        constructor(container) {
            this.container = container;
            this.groupId = container.dataset.groupId;
            this.filtersInPanel = false;

            // Mobile panel settings from data attributes
            this.mobilePanelEnabled = container.dataset.mobilePanel === '1';
            this.mobileBreakpoint = parseInt(container.dataset.mobileBreakpoint, 10) || 1000;
            this.mobileTrigger = container.dataset.mobileTrigger || 'fab';
            this.panelStyle = container.dataset.panelStyle || 'slide_out';
            this._openedFromDesktop = false;

            // Cache "Show all filters" button
            this.showAllBtn = container.querySelector('.storedash-show-all-filters-btn');

            // Cache mobile panel elements (scoped by group ID)
            if (this.mobilePanelEnabled) {
                const gid = this.groupId;
                this.fab = document.querySelector('.storedash-pf-fab[data-group-id="' + gid + '"]');
                this.overlay = document.querySelector('.storedash-pf-overlay[data-group-id="' + gid + '"]');
                this.panel = document.querySelector('.storedash-pf-panel[data-group-id="' + gid + '"]');
                this.panelContent = this.panel ? this.panel.querySelector('.storedash-pf-panel__content') : null;
                this.panelClose = this.panel ? this.panel.querySelector('.storedash-pf-panel__close') : null;

                // Cache footer elements
                this.panelFooter = this.panel ? this.panel.querySelector('.storedash-pf-panel__footer') : null;
                this.clearBtn = this.panel ? this.panel.querySelector('[data-action="clear-filters"]') : null;
                this.viewBtn = this.panel ? this.panel.querySelector('[data-action="view-products"]') : null;
                this.viewTemplate = this.panelFooter ? this.panelFooter.dataset.viewTemplate || '' : '';
                this.lastKnownCount = null;
            }

            // Store instance on container for cleanup
            if (container._storedashPF) {
                container._storedashPF.destroy();
            }
            container._storedashPF = this;

            this.init();
        }

        init() {
            // Handle responsive layout changes
            this.handleResponsiveLayout();
            // PERF FIX: Use shared resize broadcaster if available (already debounced)
            this._resizeHandler = () => this.handleResponsiveLayout();
            if (window.StoredashResize) {
                window.StoredashResize.on(this._resizeHandler);
            } else {
                const origHandler = this._resizeHandler;
                this._resizeHandler = () => {
                    clearTimeout(this._resizeTimeout);
                    this._resizeTimeout = setTimeout(origHandler, 200);
                };
                window.addEventListener('resize', this._resizeHandler);
            }

            // Initialize mobile panel if enabled (FAB may not exist in inline_button mode)
            if (this.mobilePanelEnabled && this.panel) {
                this.initMobilePanel();
            }
        }

        handleResponsiveLayout() {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                this.container.classList.add('storedash-predefined-filters--mobile');
            } else {
                this.container.classList.remove('storedash-predefined-filters--mobile');
            }

            // Handle mobile panel switching
            if (this.mobilePanelEnabled) {
                this.handleMobileSwitch();
            }
        }

        /* ==========================================
           Mobile Panel Methods
           ========================================== */

        initMobilePanel() {
            // Apply bottom-sheet class if configured (PHP also adds it, but this ensures
            // dynamically initialized panels and editor re-renders get the class too)
            if (this.panelStyle === 'bottom_sheet' && this.panel) {
                this.panel.classList.add('storedash-pf-panel--bottom-sheet');
            }

            // FAB click opens panel (FAB may not exist in inline_button mode)
            if (this.fab) {
                this._fabClickHandler = () => this.openPanel();
                this.fab.addEventListener('click', this._fabClickHandler);
            }

            // "Show all filters" button — opens panel (mobile) or clones filters into panel (desktop)
            if (this.showAllBtn) {
                this._showAllClickHandler = (e) => {
                    if (e) e.preventDefault();
                    if (this.isPanelOpen()) return;
                    if (this.filtersInPanel) {
                        // Mobile: filters already moved to panel, just open it
                        this.openPanel();
                    } else {
                        // Desktop: clone filters into panel
                        this.openPanelDesktop();
                    }
                };
                this.showAllBtn.addEventListener('click', this._showAllClickHandler);

                // Mobile fallback: touchend fires reliably on all mobile browsers
                // whereas click can be swallowed by iOS Safari in some DOM configurations
                this._showAllTouchHandler = (e) => {
                    e.preventDefault(); // prevent ghost click / double-fire
                    this._showAllClickHandler();
                };
                this.showAllBtn.addEventListener('touchend', this._showAllTouchHandler);
            }

            // Close button
            if (this.panelClose) {
                this._closeClickHandler = () => this.closePanel();
                this.panelClose.addEventListener('click', this._closeClickHandler);
            }

            // Overlay click closes panel
            if (this.overlay) {
                this._overlayClickHandler = () => this.closePanel();
                this.overlay.addEventListener('click', this._overlayClickHandler);
            }

            // PERF FIX: Escape key handler scoped to this instance (bound on element, not document).
            // Each panel instance needs its own check, but we use a lightweight per-instance handler
            // that only fires when the panel is open.
            this._escHandler = (e) => {
                if (e.key === 'Escape' && this.isPanelOpen()) {
                    this.closePanel();
                }
            };
            document.addEventListener('keydown', this._escHandler);

            // Footer buttons
            if (this.clearBtn) {
                this._clearClickHandler = () => this.clearFilters();
                this.clearBtn.addEventListener('click', this._clearClickHandler);
            }
            if (this.viewBtn) {
                this._viewClickHandler = () => this.closePanel();
                this.viewBtn.addEventListener('click', this._viewClickHandler);
            }

            // Live count: update view button text when filters change
            this._filtersChangedHandler = (e) => {
                const detail = e.detail || {};
                if (typeof detail.totalProducts !== 'undefined') {
                    this.lastKnownCount = detail.totalProducts;
                    this.updateViewButtonCount(detail.totalProducts);
                }
            };
            document.addEventListener('storedash:filters:changed', this._filtersChangedHandler);

            // Check initial state
            this.handleMobileSwitch();
        }

        handleMobileSwitch() {
            if (!this.panel) return;

            const isBelowBreakpoint = window.innerWidth <= this.mobileBreakpoint;

            // Skip if breakpoint state hasn't changed (avoids redundant DOM work)
            if (isBelowBreakpoint === this._lastBreakpointState) return;
            this._lastBreakpointState = isBelowBreakpoint;

            if (isBelowBreakpoint) {
                // If panel was opened from desktop "Show all filters", close it first
                if (this._openedFromDesktop && this.isPanelOpen()) {
                    // Clean up desktop clones synchronously before closePanel(),
                    // otherwise the delayed _removePanelClones() in closePanel's
                    // setTimeout will destroy the accordion listener that
                    // moveFiltersToPanel() → _setupAccordion() is about to create.
                    this._removePanelClones();
                    this._openedFromDesktop = false;
                    this.closePanel();
                }
                this.moveFiltersToPanel();
            } else {
                // Going back to desktop — close panel and move filters back
                if (this.isPanelOpen()) {
                    this.closePanel();
                }
                this._openedFromDesktop = false;
                this.moveFiltersBack();
            }
        }

        moveFiltersToPanel() {
            if (this.filtersInPanel || !this.panelContent) return;

            // Get all filter items from the inline container
            this.filterItems = Array.from(
                this.container.querySelectorAll('.storedash-predefined-filter-item')
            );

            // PERF FIX: Use DocumentFragment for batch DOM insertion (single reflow instead of N).
            const fragment = document.createDocumentFragment();
            this.filterItems.forEach(item => {
                item._savedStyle = item.getAttribute('style') || '';
                item.removeAttribute('style');
                fragment.appendChild(item);
            });
            this.panelContent.appendChild(fragment);

            // Set up accordion state for all filters in the panel
            this._setupAccordion();

            // Mobile fix: ensure checkbox/radio change events fire inside the panel.
            // iOS Safari can swallow change events in position:fixed scrollable containers.
            this._setupPanelInputFix();

            this.filtersInPanel = true;
        }

        moveFiltersBack() {
            if (!this.filtersInPanel || !this.filterItems) return;

            // Clean up mobile input fix and accordion state before moving back
            this._teardownPanelInputFix();
            this._teardownAccordion();

            // Move filter items back — restore inline styles for grid layout
            this.filterItems.forEach(item => {
                if (item._savedStyle) {
                    item.setAttribute('style', item._savedStyle);
                }
                delete item._savedStyle;
                this.container.appendChild(item);
            });

            this.filtersInPanel = false;
        }

        /**
         * Clone filter items into the panel (desktop "Show all filters" flow).
         * Originals stay in place so inline collapsible buttons remain visible.
         */
        _cloneFiltersToPanel() {
            if (!this.panelContent) return;

            const items = this.container.querySelectorAll('.storedash-predefined-filter-item');
            this._panelClones = [];

            // PERF FIX: Use DocumentFragment for batch DOM insertion (single reflow).
            const fragment = document.createDocumentFragment();
            items.forEach(item => {
                const clone = item.cloneNode(true);
                clone.removeAttribute('style');
                fragment.appendChild(clone);
                this._panelClones.push(clone);
            });
            this.panelContent.appendChild(fragment);

            // Initialize StoredashFilters instances after DOM insertion
            this._panelClones.forEach(clone => {
                const filterEl = clone.querySelector('.storedash-filter');
                if (filterEl && window.StoredashFilters) {
                    delete filterEl.dataset.initialized;
                    new window.StoredashFilters(filterEl);
                }
            });

            this._setupAccordion();
        }

        /**
         * Remove cloned filter content from the panel (desktop close).
         */
        _removePanelClones() {
            if (!this._panelClones) return;

            // Clean up panel-level accordion listener
            if (this._panelToggleCapture) {
                this.panelContent.removeEventListener('click', this._panelToggleCapture, true);
                this._panelToggleCapture = null;
            }

            // Destroy cloned StoredashFilters instances and remove elements
            this._panelClones.forEach(clone => {
                const filterEl = clone.querySelector('.storedash-filter');
                if (filterEl && filterEl._storedashFilterInstance) {
                    filterEl._storedashFilterInstance.destroy();
                }
                clone.remove();
            });
            this._panelClones = null;
        }

        /* ==========================================
           Accordion Methods
           ========================================== */

        _setupAccordion() {
            if (!this.panelContent) return;

            // Intercept toggle-button clicks inside the panel (capturing phase)
            // so we animate instead of the instant toggle in filters.js
            this._panelToggleCapture = (e) => {
                const toggle = e.target.closest('.storedash-filter-toggle');
                if (toggle) {
                    const filter = toggle.closest('.storedash-filter');
                    if (filter) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        this._toggleAccordion(filter);
                    }
                }
            };
            this.panelContent.addEventListener('click', this._panelToggleCapture, true);

            const filters = this.panelContent.querySelectorAll('.storedash-filter');

            filters.forEach(filter => {
                // Track original state so we can restore on moveFiltersBack
                filter.dataset.panelWasCollapsible = filter.classList.contains('collapsible') ? '1' : '0';
                filter.dataset.panelWasCollapsed = filter.classList.contains('collapsed') ? '1' : '0';

                // For collapsible_button type: inject a header since PHP doesn't render one
                if (filter.dataset.displayType === 'collapsible_button' && !filter.querySelector('.storedash-filter-header')) {
                    const titleText = filter.querySelector('.button-text')?.textContent || 'Filter';
                    const header = document.createElement('div');
                    header.className = 'storedash-filter-header';
                    header.dataset.panelInjected = '1';
                    header.innerHTML =
                        '<h3 class="storedash-filter-title">' + this._escHtml(titleText) + '</h3>' +
                        '<button class="storedash-filter-toggle" type="button" aria-label="Toggle filter">' +
                        '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                        '</button>';
                    const content = filter.querySelector('.storedash-filter-content');
                    if (content) {
                        filter.insertBefore(header, content);
                    }
                }

                // Make all filters collapsible and start collapsed
                filter.classList.add('collapsible', 'collapsed');

                // Set initial collapsed height to 0 (no transition for initial state)
                const content = filter.querySelector('.storedash-filter-content');
                if (content) {
                    content.style.transition = 'none';
                    content.style.height = '0';
                    content.style.maxHeight = '0';
                    content.style.overflow = 'hidden';
                    // Force reflow then restore transition
                    content.offsetHeight; // eslint-disable-line no-unused-expressions
                    content.style.transition = '';
                }

                // Make entire header row clickable (not just the toggle button)
                const header = filter.querySelector('.storedash-filter-header');
                if (header && !header.dataset.panelClickBound) {
                    header.addEventListener('click', (e) => {
                        if (!e.target.closest('.storedash-filter-toggle')) {
                            this._toggleAccordion(filter);
                        }
                    });
                    header.dataset.panelClickBound = '1';
                }
            });
        }

        /**
         * Smoothly expand or collapse a filter accordion in the panel.
         * Uses setTimeout (reliable) instead of transitionend (flaky).
         */
        _toggleAccordion(filter) {
            const content = filter.querySelector('.storedash-filter-content');
            if (!content || filter._animating) return;

            const isCollapsed = filter.classList.contains('collapsed');
            const duration = 250; // must match CSS transition duration
            filter._animating = true;

            if (isCollapsed) {
                // --- Expand ---
                // Remove collapsed so CSS shows display:block and chevron rotates
                filter.classList.remove('collapsed');

                // Clear maxHeight constraint so it doesn't limit expansion
                content.style.maxHeight = '';

                // Measure natural height (content is display:block, height:0)
                content.style.height = 'auto';
                const fullHeight = content.scrollHeight;

                // Snap back to 0 and animate to full height
                content.style.height = '0';
                content.offsetHeight; // force reflow
                content.style.height = fullHeight + 'px';

                // After animation, switch to auto height for natural reflow
                setTimeout(() => {
                    content.style.height = 'auto';
                    filter._animating = false;
                }, duration);
            } else {
                // --- Collapse ---
                // Lock current height so we can transition from it
                content.style.height = content.scrollHeight + 'px';
                content.offsetHeight; // force reflow

                // Animate to 0
                content.style.height = '0';
                content.style.maxHeight = '0';

                // After animation, add collapsed class (rotates chevron)
                setTimeout(() => {
                    filter.classList.add('collapsed');
                    filter._animating = false;
                }, duration);
            }
        }

        _teardownAccordion() {
            if (!this.panelContent) return;

            // Remove the capturing toggle listener
            if (this._panelToggleCapture) {
                this.panelContent.removeEventListener('click', this._panelToggleCapture, true);
                this._panelToggleCapture = null;
            }

            const filters = this.panelContent.querySelectorAll('.storedash-filter');

            filters.forEach(filter => {
                // Remove injected headers
                const injectedHeader = filter.querySelector('.storedash-filter-header[data-panel-injected="1"]');
                if (injectedHeader) {
                    injectedHeader.remove();
                }

                // Clean up inline height styles from animation
                const content = filter.querySelector('.storedash-filter-content');
                if (content) {
                    content.style.height = '';
                    content.style.maxHeight = '';
                    content.style.overflow = '';
                    content.style.transition = '';
                }

                // Clear animation lock
                delete filter._animating;

                // Restore original collapsible/collapsed state
                if (filter.dataset.panelWasCollapsible === '0') {
                    filter.classList.remove('collapsible');
                }
                if (filter.dataset.panelWasCollapsed === '0') {
                    filter.classList.remove('collapsed');
                }

                // Clean up data attributes
                delete filter.dataset.panelWasCollapsible;
                delete filter.dataset.panelWasCollapsed;
            });
        }

        /**
         * Mobile fix: iOS Safari can swallow change events on checkboxes/radios
         * inside position:fixed scrollable containers. We listen for click on
         * inputs and use a flag to detect if the native change event fired.
         * If it didn't fire within a short window, we dispatch it manually.
         */
        _setupPanelInputFix() {
            if (!this.panelContent) return;

            // Track whether native change fired for this input
            this._panelChangeHandler = (e) => {
                const input = e.target;
                if (input && (input.type === 'checkbox' || input.type === 'radio')) {
                    input._storedashChangeFired = true;
                }
            };
            this.panelContent.addEventListener('change', this._panelChangeHandler, true);

            this._panelInputClickHandler = (e) => {
                const input = e.target.closest('input[type="checkbox"], input[type="radio"]');
                if (!input) return;

                // Reset the flag before the native change event has a chance to fire
                input._storedashChangeFired = false;

                // After a microtask, check if native change fired. If not, dispatch manually.
                setTimeout(() => {
                    if (!input._storedashChangeFired) {
                        input.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    delete input._storedashChangeFired;
                }, 0);
            };
            this.panelContent.addEventListener('click', this._panelInputClickHandler);

            // Also handle taps on filter buttons and swatches (touchend fallback)
            this._panelTouchHandler = (e) => {
                const btn = e.target.closest('.storedash-filter-button, .storedash-filter-swatch');
                if (!btn) return;
                e.preventDefault();
                btn.click();
            };
            this.panelContent.addEventListener('touchend', this._panelTouchHandler);
        }

        _teardownPanelInputFix() {
            if (this.panelContent && this._panelChangeHandler) {
                this.panelContent.removeEventListener('change', this._panelChangeHandler, true);
                this._panelChangeHandler = null;
            }
            if (this.panelContent && this._panelInputClickHandler) {
                this.panelContent.removeEventListener('click', this._panelInputClickHandler);
                this._panelInputClickHandler = null;
            }
            if (this.panelContent && this._panelTouchHandler) {
                this.panelContent.removeEventListener('touchend', this._panelTouchHandler);
                this._panelTouchHandler = null;
            }
        }

        _escHtml(str) {
            return String(str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        }

        isPanelOpen() {
            return this.panel && this.panel.classList.contains('storedash-pf-panel--open');
        }

        openPanel() {
            if (!this.panel || this.isPanelOpen()) return;
            this._openedFromDesktop = false;

            // Show elements with display first (display:none → visible)
            this.panel.style.display = 'flex';
            if (this.overlay) {
                this.overlay.style.display = 'block';
            }

            // Double-rAF ensures browser commits display change before animating
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (this.overlay) {
                        this.overlay.classList.add('storedash-pf-overlay--visible');
                    }
                    this.panel.classList.add('storedash-pf-panel--open');
                });
            });

            // Update aria
            if (this.fab) {
                this.fab.setAttribute('aria-expanded', 'true');
            }

            // Prevent body scroll (shared counter for multiple panels)
            if (!window.__storedashPanelOpenCount) {
                this._previousBodyOverflow = document.body.style.overflow;
                window.__storedashPanelOpenCount = 0;
            }
            window.__storedashPanelOpenCount++;
            document.body.style.overflow = 'hidden';

            // Focus close button
            if (this.panelClose) {
                this.panelClose.focus();
            }

            // Set up focus trap (cache focusable elements to avoid reflow per keypress)
            this._cachedFocusable = null;
            this._focusTrapHandler = (e) => this._trapFocus(e);
            document.addEventListener('keydown', this._focusTrapHandler);
        }

        /**
         * Open panel from desktop "Show all filters" button.
         * Clones filters into the panel so the inline collapsible buttons stay visible.
         */
        openPanelDesktop() {
            if (!this.panel || this.isPanelOpen()) return;

            this._openedFromDesktop = true;

            // Clone filters into the panel (originals stay in place)
            this._cloneFiltersToPanel();

            // Show elements with display first (display:none → visible)
            this.panel.style.display = 'flex';
            if (this.overlay) {
                this.overlay.style.display = 'block';
            }

            // Double-rAF ensures browser commits display change before animating
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (this.overlay) {
                        this.overlay.classList.add('storedash-pf-overlay--visible');
                    }
                    this.panel.classList.add('storedash-pf-panel--open');
                });
            });

            // Update aria on showAllBtn
            if (this.showAllBtn) {
                this.showAllBtn.setAttribute('aria-expanded', 'true');
            }

            // Prevent body scroll
            if (!window.__storedashPanelOpenCount) {
                this._previousBodyOverflow = document.body.style.overflow;
                window.__storedashPanelOpenCount = 0;
            }
            window.__storedashPanelOpenCount++;
            document.body.style.overflow = 'hidden';

            // Focus close button
            if (this.panelClose) {
                this.panelClose.focus();
            }

            // Set up focus trap
            this._cachedFocusable = null;
            this._focusTrapHandler = (e) => this._trapFocus(e);
            document.addEventListener('keydown', this._focusTrapHandler);
        }

        closePanel() {
            if (!this.panel || !this.isPanelOpen()) return;

            const wasDesktop = this._openedFromDesktop;

            // Remove focus trap
            if (this._focusTrapHandler) {
                document.removeEventListener('keydown', this._focusTrapHandler);
                this._focusTrapHandler = null;
            }

            // Animate out
            if (this.overlay) {
                this.overlay.classList.remove('storedash-pf-overlay--visible');
            }
            this.panel.classList.remove('storedash-pf-panel--open');

            // After animation completes, hide with display:none and clean up
            setTimeout(() => {
                this.panel.style.display = 'none';
                if (this.overlay) {
                    this.overlay.style.display = 'none';
                }
                // Desktop: remove cloned filter content from panel
                if (wasDesktop) {
                    this._removePanelClones();
                }
            }, 300); // Match CSS transition duration

            // Update aria
            if (wasDesktop && this.showAllBtn) {
                this.showAllBtn.setAttribute('aria-expanded', 'false');
            }
            if (this.fab) {
                this.fab.setAttribute('aria-expanded', 'false');
            }

            // Restore body scroll only when all panels are closed
            if (window.__storedashPanelOpenCount) {
                window.__storedashPanelOpenCount--;
            }
            if (!window.__storedashPanelOpenCount) {
                document.body.style.overflow = this._previousBodyOverflow || '';
                this._previousBodyOverflow = undefined;
            }

            // Return focus to appropriate trigger
            if (wasDesktop && this.showAllBtn) {
                this.showAllBtn.focus();
            } else if (this.fab) {
                this.fab.focus();
            } else if (this.showAllBtn) {
                this.showAllBtn.focus();
            }

            this._openedFromDesktop = false;
        }

        /* ==========================================
           Footer Methods
           ========================================== */

        clearFilters() {
            // Remove filter_* URL params
            const url = new URL(window.location.href);
            const keysToRemove = [];
            for (const key of url.searchParams.keys()) {
                if (key.startsWith('filter_')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => url.searchParams.delete(key));

            // Reset all filter widget states within the panel
            const panel = this.panelContent || this.container;
            panel.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(input => {
                input.checked = false;
            });
            panel.querySelectorAll('select').forEach(select => {
                select.selectedIndex = 0;
            });
            panel.querySelectorAll('.active').forEach(el => {
                el.classList.remove('active');
            });

            // Push clean URL with storedash state marker so filters.js popstate handler runs AJAX
            const clearState = {
                filters: {},
                timestamp: Date.now(),
                storedashFilters: true,
                cleared: true,
                source: 'panel-clear'
            };
            window.history.pushState(clearState, '', url.toString());

            // Dispatch event so the filter engine re-fetches products
            document.dispatchEvent(new CustomEvent('storedash:filters:changed', {
                detail: { filters: {}, filter_counts: {}, cleared: true, source: 'panel-clear' }
            }));

            // Trigger popstate for filters.js to pick up the URL change
            window.dispatchEvent(new PopStateEvent('popstate', { state: clearState }));
        }

        updateViewButtonCount(count) {
            if (!this.viewBtn || !this.viewTemplate) return;

            if (count === null || count === undefined) {
                // No count available — show template without {count}
                const text = this.viewTemplate
                    .replace(' {count}', '')
                    .replace('{count} ', '')
                    .replace('{count}', '');
                this.viewBtn.textContent = text;
            } else {
                this.viewBtn.textContent = this.viewTemplate.replace('{count}', count);
            }
        }

        _getFocusableElements() {
            if (!this.panel) return [];
            if (this._cachedFocusable) return this._cachedFocusable;
            this._cachedFocusable = Array.from(this.panel.querySelectorAll(
                'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
            )).filter(el => !el.hidden);
            return this._cachedFocusable;
        }

        _trapFocus(e) {
            if (e.key !== 'Tab') return;
            const focusable = this._getFocusableElements();
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        destroy() {
            // Close panel if open (restores body scroll, removes focus trap)
            if (this.isPanelOpen()) {
                this.closePanel();
            }

            // Remove document-level listeners
            if (this._escHandler) {
                document.removeEventListener('keydown', this._escHandler);
                this._escHandler = null;
            }
            if (this._filtersChangedHandler) {
                document.removeEventListener('storedash:filters:changed', this._filtersChangedHandler);
                this._filtersChangedHandler = null;
            }

            // Remove window-level listeners
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
                this._resizeHandler = null;
            }
            clearTimeout(this._resizeTimeout);

            // Remove element-level listeners
            if (this.showAllBtn && this._showAllClickHandler) {
                this.showAllBtn.removeEventListener('click', this._showAllClickHandler);
                this._showAllClickHandler = null;
            }
            if (this.showAllBtn && this._showAllTouchHandler) {
                this.showAllBtn.removeEventListener('touchend', this._showAllTouchHandler);
                this._showAllTouchHandler = null;
            }
            if (this.fab && this._fabClickHandler) {
                this.fab.removeEventListener('click', this._fabClickHandler);
                this._fabClickHandler = null;
            }
            if (this.panelClose && this._closeClickHandler) {
                this.panelClose.removeEventListener('click', this._closeClickHandler);
                this._closeClickHandler = null;
            }
            if (this.overlay && this._overlayClickHandler) {
                this.overlay.removeEventListener('click', this._overlayClickHandler);
                this._overlayClickHandler = null;
            }
            if (this.clearBtn && this._clearClickHandler) {
                this.clearBtn.removeEventListener('click', this._clearClickHandler);
                this._clearClickHandler = null;
            }
            if (this.viewBtn && this._viewClickHandler) {
                this.viewBtn.removeEventListener('click', this._viewClickHandler);
                this._viewClickHandler = null;
            }

            // Remove focus trap if still attached
            if (this._focusTrapHandler) {
                document.removeEventListener('keydown', this._focusTrapHandler);
                this._focusTrapHandler = null;
            }

            // Clear references
            this.lastKnownCount = null;
            if (this.container) {
                delete this.container._storedashPF;
            }
        }
    }

    /**
     * Initialize predefined filters on page load
     */
    function initPredefinedFilters() {
        const containers = document.querySelectorAll('.storedash-predefined-filters');
        containers.forEach(container => {
            if (!container.dataset.predefinedFiltersInitialized) {
                new PredefinedFilters(container);
                container.dataset.predefinedFiltersInitialized = 'true';
            }
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPredefinedFilters);
    } else {
        initPredefinedFilters();
    }

    // Re-initialize after AJAX filter updates (filters.js dispatches this)
    document.addEventListener('storedash:filters:updated', function() {
        initPredefinedFilters();
    });

    // Also initialize when Elementor frontend is ready
    if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
        elementorFrontend.hooks.addAction('frontend/element_ready/storedash-predefined-filters.default', function($scope) {
            // Handle both jQuery object and native element
            const element = $scope[0] || $scope;
            const container = element.querySelector ? element.querySelector('.storedash-predefined-filters') : null;
            if (container && !container.dataset.predefinedFiltersInitialized) {
                new PredefinedFilters(container);
                container.dataset.predefinedFiltersInitialized = 'true';
            }
        });
    }

    // Export for global access
    window.Storedash = window.Storedash || {};
    window.Storedash.PredefinedFilters = PredefinedFilters;
    window.StoredashPredefinedFilters = PredefinedFilters; // backward compat

    /**
     * Bricks Builder editor integration
     * Called by Bricks via $scripts = ['storedashBricksPredefinedFilters'] when the
     * element renders or re-renders in the editor.
     */
    window.storedashBricksPredefinedFilters = function(element) {
        if (!element) return;
        const container = element.querySelector('.storedash-predefined-filters');
        if (container) {
            // destroy() is called automatically in the constructor via container._storedashPF
            delete container.dataset.predefinedFiltersInitialized;
            new PredefinedFilters(container);
            container.dataset.predefinedFiltersInitialized = 'true';
        }
    };

})();

